import { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { boundingBoxesOverlap } from "../../../../common/BoundingBox";
import { devModeLog } from "../../../../common/devModeLog";
import { Point2D } from "../../../../common/Point2D";
import { snapPoint2DToGrid } from "../../../../common/snapToGrid";
import { useDrag } from "../../../../common/useDrag";
import { useSelect } from "../../../../common/useSelect";
import { useZoom } from "../../../../common/useZoom";
import {
	createWindow,
	selectOpenSearch,
} from "../../../windowManager/windowManagerSlice";
import {
	addUndo,
	cancelPreview,
	createConnections,
	createDeviceSlot,
	createMemoryRefPreview,
	createMemoryRefs,
	createOperatorPreview,
	createOperators,
	deleteSelection,
	insertMemory,
	insertPreview,
	moveSheetByOffset,
	selectDevices,
	selectMemories,
	selectMultiple,
	selectSheet,
	setSelection,
	setSheetGridSize,
	setSideBarSelection,
	updateBlockPreviewBoundingBox,
	updateDeviceSlot,
	updateSheetScale,
} from "../../editorSlice";
import {
	isInSelection,
	SelectionConnection,
	SelectionMemoryRef,
	SelectionOperator,
} from "../../interfaces/Sheet";
import { Block, BlockPreview } from "./block/Block";
import {
	blockFromIo,
	ConnectionLine,
	ioHeightSlot,
} from "./connection/Connection";
import { InfoBox } from "./infoBox/InfoBox";
import css from "./Sheet.module.css";
import { SheetBackground } from "./sheetBackground/SheetBackground";

import { nonNullableFilter } from "../../../../common/nonNullableFilter";
import { selectLogics } from "../../../logics/logicsSlice";
import Connection from "../../interfaces/components/Connection";
import Device from "../../interfaces/components/Device";
import Memory, {
	MemorySourceAddress,
} from "../../interfaces/components/Memory";
import MemoryRef from "../../interfaces/components/MemoryRef";
import Operator from "../../interfaces/components/Operator";
import { Search } from "./search/Search";

const MEMORY_WINDOW_SIZES = {
	MESSAGE: { x: 800, y: 600 },
	IMAGE: { x: 800, y: 600 },
	DEFAULT: { x: 500, y: 400 },
} as Record<string, { x: number; y: number }>;

interface BlockStorage {
	memories: MemoryRef[];
	operators: Operator[];
	connections: Connection[];
}

interface DeviceStorage {
	devices: Device[];
	memories: Memory[];
}

export interface SheetProps {
	sheetIndex: number;
}

export const Sheet: React.FC<SheetProps> = (props) => {
	const dispatch = useAppDispatch();
	const logics = useAppSelector(selectLogics);
	const editorMemories = useAppSelector(selectMemories);
	const editorDevices = useAppSelector(selectDevices);
	const openSearch = useAppSelector(selectOpenSearch);

	const [selectionRect, setSelectionRect] = useState<{
		x: number;
		y: number;
		width: number;
		height: number;
	} | null>(null);
	const [startPos, setStartPos] = useState<{
		grid: {
			x: number;
			y: number;
		};
		screen: {
			x: number;
			y: number;
		};
	} | null>(null);

	const sideBarSelection = useAppSelector(
		(state) => state.editor.sideBarSelection,
	);
	const sheet = useAppSelector(selectSheet(props.sheetIndex));
	const gridPixelSize = sheet.gridPixelSize;
	const blockPreview = useAppSelector((state) => state.editor.blockPreview);
	const blockPreviewTrigger = useAppSelector(
		(state) => state.editor.blockPreviewTrigger,
	);

	const { zoomIn, zoomOut, fixedZoom } = useZoom({
		maxAbsoluteScaling: 3,
		maxAbsoluteLevel: 5,
	});

	const sheetScale = sheet.scale;
	const selectionRef = useRef<HTMLDivElement>(null);
	const sheetWindowRef = useRef<HTMLDivElement>(null);
	const [ref, setRef] = useState<HTMLDivElement | null>(null);
	const previewRef = useRef<HTMLDivElement>(null);

	const sheetPosition = sheet.position;

	const sheetSize = {
		x: (ref?.getBoundingClientRect().width ?? 0) / sheetScale,
		y: (ref?.getBoundingClientRect().height ?? 0) / sheetScale,
	};

	useEffect(() => {
		if (ref === null) {
			return;
		}

		const resizeObserver = new ResizeObserver(([observed]) => {
			dispatch(
				setSheetGridSize({
					index: props.sheetIndex,
					size: {
						x: observed.contentRect.width,
						y: observed.contentRect.height,
					},
				}),
			);
		});

		resizeObserver.observe(ref);

		return () => resizeObserver.disconnect();
	}, [props.sheetIndex, ref]);

	const sheetBoundingBox = {
		left: sheet.position.x - sheetSize.x / 2,
		top: sheet.position.y - sheetSize.y / 2,
		width: sheetSize.x,
		height: sheetSize.y,
	};

	const validateSelection = (
		rectangle: {
			x: { start: number; end: number };
			y: { start: number; end: number };
		},
		x: number,
		y: number,
	) => {
		return (
			rectangle.x.start < x &&
			rectangle.x.end > x &&
			rectangle.y.start < y &&
			rectangle.y.end > y
		);
	};

	const visibleOperators = useMemo(
		() =>
			sheet.operators.filter((operator, index) => {
				const isInView = boundingBoxesOverlap(
					operator.boundingBox,
					sheetBoundingBox,
				);
				const isSelected = isInSelection(
					{
						index,
						type: "operator",
						operatorId: operator.id,
					},
					sheet.selection,
				);
				return isInView || isSelected;
			}),
		[sheet.operators, sheetBoundingBox],
	);

	const visibleMemoryRefs = useMemo(
		() =>
			sheet.memoryRefs.filter((memoryRefs) =>
				boundingBoxesOverlap(memoryRefs.boundingBox, sheetBoundingBox),
			),
		[sheet.memoryRefs, sheetBoundingBox],
	);

	const canPlacePreview = useMemo(() => {
		if (!blockPreview) {
			return false;
		}

		const previewBoundingBox = blockPreview.block.boundingBox;

		const overlapsVisibleOperators = visibleOperators.some((operator) => {
			return boundingBoxesOverlap(operator.boundingBox, previewBoundingBox, {
				tolerance: 1,
			});
		});

		const overlapsVisibleMemoryRefs = visibleMemoryRefs.some((memoryRef) => {
			return boundingBoxesOverlap(memoryRef.boundingBox, previewBoundingBox, {
				tolerance: 1,
			});
		});

		return !overlapsVisibleOperators && !overlapsVisibleMemoryRefs;
	}, [blockPreview?.type, visibleOperators]);

	const onSelectionDragStart = useSelect(
		{
			onMouseDown({ event }) {
				if (!ref) return;

				const sheetBoundingRect = ref.getBoundingClientRect();

				const eventSourcePosition = {
					x: event.clientX - sheetBoundingRect.left,
					y: event.clientY - sheetBoundingRect.top,
				};

				const sheetOffset = {
					x: sheetPosition.x - sheetSize.x / 2 - gridPixelSize - right,
					y: sheetPosition.y - sheetSize.y / 2 - gridPixelSize - bottom,
				};

				const coordinatesGrid = snapPoint2DToGrid(
					{
						x: eventSourcePosition.x / sheetScale + sheetOffset.x,
						y: eventSourcePosition.y / sheetScale + sheetOffset.y,
					},
					gridPixelSize,
				);

				setStartPos({
					grid: coordinatesGrid,
					screen: {
						x: eventSourcePosition.x,
						y: eventSourcePosition.y,
					},
				});
			},
			onMouseMove({ event }) {
				if (!startPos || !ref) return;

				const sheetBoundingRect = ref.getBoundingClientRect();

				const current = {
					x: event.clientX - sheetBoundingRect.left,
					y: event.clientY - sheetBoundingRect.top,
				};

				const x = Math.min(startPos.screen.x, current.x) / sheetScale;
				const y = Math.min(startPos.screen.y, current.y) / sheetScale;
				const width = Math.abs(current.x - startPos.screen.x) / sheetScale;
				const height = Math.abs(current.y - startPos.screen.y) / sheetScale;

				setSelectionRect({
					x,
					y,
					width,
					height,
				});
			},
			onMouseUp({ totalDelta }) {
				setStartPos(null);

				if (!ref || !selectionRect || !startPos) {
					setSelectionRect(null);
					return;
				}

				ref.style.cursor = "default";

				const startXRect = Math.min(
					startPos.grid.x + totalDelta.x / sheetScale,
					startPos.grid.x,
				);
				const startYRect = Math.min(
					startPos.grid.y + totalDelta.y / sheetScale,
					startPos.grid.y,
				);
				const endXRect = startXRect + Math.abs(totalDelta.x) / sheetScale;
				const endYRect = startYRect + Math.abs(totalDelta.y) / sheetScale;

				const selectionRectangle = {
					x: { start: startXRect, end: endXRect },
					y: { start: startYRect, end: endYRect },
				};

				const memoriesInside = [] as SelectionMemoryRef[];
				const operatorsInside = [] as SelectionOperator[];
				const connectionsInside = [] as SelectionConnection[];

				sheet.memoryRefs.forEach((memory, index) => {
					const x = memory.boundingBox.left + memory.boundingBox.width / 2;
					const y = memory.boundingBox.top + memory.boundingBox.height / 2;

					if (validateSelection(selectionRectangle, x, y)) {
						memoriesInside.push({
							index,
							memoryRefId: memory.id,
							memory: memory.sourceMemoryId,
							label: memory.name,
							type: "memoryRef",
						});
					}
				});

				sheet.operators.forEach((operator, index) => {
					const x = operator.boundingBox.left + operator.boundingBox.width / 2;
					const y = operator.boundingBox.top + operator.boundingBox.height / 2;

					if (validateSelection(selectionRectangle, x, y)) {
						operatorsInside.push({
							index,
							operatorId: operator.id,
							type: "operator",
						});
					}
				});

				sheet.connections.forEach((connection, index) => {
					const { from: origin, to: destination } = connection;

					const originBlock = blockFromIo(origin, sheet);
					const destinationBlock = blockFromIo(destination, sheet);

					if (!originBlock || !destinationBlock) return;

					const originY =
						originBlock.boundingBox.top +
						(1 + 2 * ioHeightSlot(origin, originBlock, logics)) *
							sheet.gridPixelSize;
					const destinationY =
						destinationBlock.boundingBox.top +
						(1 + 2 * ioHeightSlot(destination, destinationBlock, logics)) *
							sheet.gridPixelSize;

					const originX =
						originBlock.boundingBox.left + originBlock.boundingBox.width - 1;
					const destinationX = destinationBlock.boundingBox.left + 1;

					if (
						validateSelection(selectionRectangle, originX, originY) &&
						validateSelection(selectionRectangle, destinationX, destinationY)
					) {
						connectionsInside.push({
							index,
							connectionId: connection.id,
							type: "connection",
						});
					}
				});

				dispatch(
					selectMultiple([
						...memoriesInside,
						...operatorsInside,
						...connectionsInside,
					]),
				);

				setSelectionRect(null);
			},
		},
		[selectionRect, startPos],
	);

	const onSheetDragStart = useDrag({
		onMouseDown() {
			if (ref) {
				ref.style.cursor = "grabbing";
			}

			dispatch(setSelection(null));
		},
		onMouseMove({ delta }) {
			dispatch(
				moveSheetByOffset({
					index: props.sheetIndex,
					delta: {
						x: -delta.x / sheetScale,
						y: -delta.y / sheetScale,
					},
				}),
			);
		},
		onMouseUp() {
			if (ref) {
				ref.style.cursor = "default";
			}
		},
	});

	const onStartBlockCreation = useDrag({
		onMouseUp({ event, didMouseMove }) {
			if (ref === null) {
				return;
			}

			if (didMouseMove) {
				return;
			}

			if (sheet.selection !== null) {
				return;
			}

			if (blockPreview) {
				if (canPlacePreview && blockPreviewTrigger === "click") {
					dispatch(insertPreview());
				}

				return;
			}

			if (sideBarSelection === null) {
				return;
			}

			const boundingRect = ref.getBoundingClientRect();

			const source = {
				x: event.clientX - boundingRect.left,
				y: event.clientY - boundingRect.top,
			};

			const target = {
				x:
					source.x / sheetScale +
					sheetPosition.x -
					sheetSize.x / 2 -
					gridPixelSize -
					right,
				y:
					source.y / sheetScale +
					sheetPosition.y -
					sheetSize.y / 2 -
					gridPixelSize -
					bottom,
			};

			switch (sideBarSelection.type) {
				case "operator":
					dispatch(
						createOperatorPreview(
							sideBarSelection.label,
							props.sheetIndex,
							target,
							"click",
						),
					);
					break;
				case "memory":
					dispatch(
						createMemoryRefPreview(
							sideBarSelection.label,
							props.sheetIndex,
							target,
							"click",
						),
					);
					break;
				default:
					console.error("Unknown side bar selection type");
			}
		},
		preventOnMouseUpIf(event) {
			return event.button !== 0;
		},
	});

	const positionWithOffset: Point2D = {
		x: sheetPosition.x - sheetSize.x / 2,
		y: sheetPosition.y - sheetSize.y / 2,
	};

	const bottom = (positionWithOffset.y % gridPixelSize) - gridPixelSize;
	const right = (positionWithOffset.x % gridPixelSize) - gridPixelSize;

	const previousBlockPreview = useRef(blockPreview);

	useEffect(() => {
		if (
			previewRef.current !== null &&
			blockPreview !== null &&
			previousBlockPreview.current === null
		) {
			devModeLog("Initial preview snap");

			const previewBoundingRect = previewRef.current.getBoundingClientRect();
			devModeLog(previewBoundingRect);

			const newGridPosition = snapPoint2DToGrid(
				{
					x:
						blockPreview.block.boundingBox.left -
						(previewBoundingRect.width * 0.5) / sheetScale,
					y:
						blockPreview.block.boundingBox.top -
						(previewBoundingRect.height * 0.5) / sheetScale,
				},
				gridPixelSize,
			);

			const newBoundingBox = {
				height: previewBoundingRect.height,
				width: previewBoundingRect.width,
				left: newGridPosition.x,
				top: newGridPosition.y,
			};

			devModeLog(newBoundingBox);

			dispatch(updateBlockPreviewBoundingBox(newBoundingBox));
		}

		previousBlockPreview.current = blockPreview;

		const onMouseMove = (event: MouseEvent) => {
			if (!blockPreview || !ref || !previewRef.current) {
				return;
			}

			const sheetBoundingRect = ref.getBoundingClientRect();
			const previewBoundingRect = previewRef.current.getBoundingClientRect();

			const eventSourcePosition = {
				x: event.clientX - sheetBoundingRect.left,
				y: event.clientY - sheetBoundingRect.top,
			};

			const sheetOffset = {
				x: sheetPosition.x - sheetSize.x / 2 - gridPixelSize - right,
				y: sheetPosition.y - sheetSize.y / 2 - gridPixelSize - bottom,
			};

			const newGridPosition = snapPoint2DToGrid(
				{
					x:
						(eventSourcePosition.x - previewBoundingRect.width * 0.5) /
							sheetScale +
						sheetOffset.x,
					y:
						(eventSourcePosition.y - previewBoundingRect.height * 0.5) /
							sheetScale +
						sheetOffset.y,
				},
				gridPixelSize,
			);

			const newBoundingBox = {
				height: previewBoundingRect.height,
				width: previewBoundingRect.width,
				left: newGridPosition.x,
				top: newGridPosition.y,
			};

			if (blockPreview !== null) {
				dispatch(updateBlockPreviewBoundingBox(newBoundingBox));
			}
		};

		window.addEventListener("mousemove", onMouseMove);

		return () => window.removeEventListener("mousemove", onMouseMove);
	}, [blockPreview?.type, sheetPosition, sheetScale]);

	const createBlocks = (
		operators: Operator[],
		memories: MemoryRef[],
		connections: Connection[],
	) => {
		dispatch(createOperators(operators));
		dispatch(createMemoryRefs(memories));
		dispatch(createConnections(props.sheetIndex, connections));
	};

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				dispatch(cancelPreview());
				dispatch(setSideBarSelection(null));
			}
		};

		window.addEventListener("keydown", onKeyDown);

		return () => window.removeEventListener("keydown", onKeyDown);
	}, []);

	useEffect(() => {
		const onCopy = (event: ClipboardEvent) => {
			const checkModalOpen = window.document.getElementById("typedWindows");

			if (!checkModalOpen) {
				event.preventDefault();

				const memoriesRefs = [] as MemoryRef[];
				const operators = [] as Operator[];
				const connections = [] as Connection[];
				const memories: Set<Memory> = new Set();
				const devices: Set<Device> = new Set();

				if (sheet.selection) {
					let selection =
						sheet.selection.type == "multiple"
							? sheet.selection.selection
							: [sheet.selection];

					selection.forEach((item) => {
						if (item === null) return;

						switch (item.type) {
							case "operator":
								operators.push(sheet.operators[item.index]);
								break;
							case "memoryRef":
								if (!item.label) return;

								const memory = editorMemories[item.label].find(
									(memo) => memo.id == item.memory,
								);

								const device = editorDevices.find(
									(dev) => dev?.id == memory?.source.deviceId,
								);

								memories.add(memory!);
								devices.add(device!);
								memoriesRefs.push(sheet.memoryRefs[item.index]);
								break;
							case "connection":
								connections.push(sheet.connections[item.index]);
								break;
						}
					});

					localStorage.setItem(
						"panda-configurator-blocks",
						JSON.stringify({
							memories: memoriesRefs,
							operators,
							connections,
						}),
					);

					localStorage.setItem(
						"panda-configurator-devices",
						JSON.stringify({
							devices: Array.from(devices),
							memories: Array.from(memories),
						}),
					);

					dispatch(setSelection(null));
				}
			}
		};

		const onPaste = (event: ClipboardEvent) => {
			const checkModalOpen = window.document.getElementById("typedWindows");

			if (!checkModalOpen) {
				event.preventDefault();

				const copiedBlocks = localStorage.getItem("panda-configurator-blocks");
				const copiedDevices = localStorage.getItem(
					"panda-configurator-devices",
				);

				if (!copiedBlocks) return;

				let fromToDevices: Record<string, string> = {};

				const {
					memories: memoriesRefs,
					operators,
					connections,
				} = JSON.parse(copiedBlocks) as BlockStorage;

				dispatch(addUndo());

				if (copiedDevices) {
					const { devices, memories } = JSON.parse(
						copiedDevices,
					) as DeviceStorage;

					devices.forEach((device) => {
						const foundDevice = editorDevices.find((dev) => {
							return dev?.address == device?.address;
						});

						if (foundDevice) {
							fromToDevices = {
								...fromToDevices,
								[device!.id]: foundDevice.id,
							};
						}
					});

					let lastIndex = editorDevices.length;

					devices.forEach((device) => {
						if (!device) return;

						const sameAddressIndex = editorDevices.findIndex(
							(dev) => dev?.address == device?.address,
						);

						if (sameAddressIndex === -1) {
							dispatch(createDeviceSlot());

							dispatch(updateDeviceSlot({ index: lastIndex, device: device }));

							lastIndex++;
						}
					});

					memories.forEach((memory) => {
						if (!memory) return;

						const memoryExists = editorMemories[memory.name].find((memo) => {
							const newDevice = fromToDevices[memory?.source?.deviceId || ""];
							switch (memory.source.type) {
								case "address":
									return (
										memory.label === memo.label &&
										newDevice === memo.source.deviceId
									);
								case "local":
									return memory.label === memo.label && memory.id === memo.id;
								case "io":
									if (memo.source.type != "io") return false;

									return (
										memory.source.type === "io" &&
										memo.source.io === memory.source.io &&
										newDevice === memo.source.deviceId
									);
							}
						});

						if (!memoryExists) {
							if (
								memory.source.type == "address" ||
								memory.source.type == "io"
							) {
								const category = editorMemories[memory.name]
									.map((memory) => memory.source)
									.filter(nonNullableFilter)
									.filter(
										(source) => "address" in source,
									) as MemorySourceAddress[];

								const existingAddresses = category
									.map((source) => source.address)
									.toSorted((a, b) => a - b);
								let address = existingAddresses.findIndex(
									(address, index) => address !== index,
								);

								if (address === -1) {
									address = existingAddresses.length;
								}

								dispatch(
									insertMemory({
										memory: {
											...memory,
											source: {
												...memory.source,
												...(memory.source.type == "io" ? {} : { address }),
												deviceId: fromToDevices[memory.source.deviceId],
											},
										},
									}),
								);
							} else {
								dispatch(insertMemory({ memory: memory }));
							}
						}
					});
				}

				createBlocks(operators, memoriesRefs, connections);
			}
		};

		window.addEventListener("copy", onCopy);
		window.addEventListener("paste", onPaste);

		return () => {
			window.removeEventListener("copy", onCopy);
			window.removeEventListener("paste", onPaste);
		};
	}, [sheet, editorDevices, editorMemories]);

	return (
		<div
			ref={sheetWindowRef}
			className={css.sheetWindow}
			tabIndex={0}
			onKeyDown={(event) => {
				if (event.key === "Delete") {
					dispatch(deleteSelection());
				}
			}}
			onWheel={(event) => {
				event.stopPropagation();
				const sheetBoundingRect = ref!.getBoundingClientRect();

				const eventSourcePosition = {
					x: event.clientX - sheetBoundingRect.left,
					y: event.clientY - sheetBoundingRect.top,
				};

				const sheetOffset = {
					x: sheetPosition.x - sheetSize.x / 2 - gridPixelSize - right,
					y: sheetPosition.y - sheetSize.y / 2 - gridPixelSize - bottom,
				};

				const focus = {
					x: eventSourcePosition.x / sheetScale + sheetOffset.x,
					y: eventSourcePosition.y / sheetScale + sheetOffset.y,
				};

				const newScale = event.deltaY < 0 ? zoomIn() : zoomOut();
				const scalingRatio = newScale / sheetScale;

				const movement = {
					x: ((sheetPosition.x - focus.x) * (1 - scalingRatio)) / scalingRatio,
					y: ((sheetPosition.y - focus.y) * (1 - scalingRatio)) / scalingRatio,
				};

				dispatch(
					moveSheetByOffset({
						index: props.sheetIndex,
						delta: movement,
					}),
				);

				dispatch(
					updateSheetScale({
						index: props.sheetIndex,
						scale: newScale,
					}),
				);

				if (blockPreview) {
					const previewBoundingRect =
						previewRef.current!.getBoundingClientRect();

					const newGridPosition = snapPoint2DToGrid(
						{
							x:
								(eventSourcePosition.x - previewBoundingRect.width * 0.5) /
									sheetScale +
								sheetOffset.x,
							y:
								(eventSourcePosition.y - previewBoundingRect.height * 0.5) /
									sheetScale +
								sheetOffset.y,
						},
						gridPixelSize,
					);

					const newBoundingBox = {
						height: previewBoundingRect.height,
						width: previewBoundingRect.width,
						left: newGridPosition.x,
						top: newGridPosition.y,
					};

					dispatch(updateBlockPreviewBoundingBox(newBoundingBox));
				}
			}}
		>
			{openSearch && <Search fixedZoom={fixedZoom} />}
			<div
				className={css.sheetInternalWindow}
				style={{
					transform: `scale(${sheetScale})`,
					width: `${100 / sheetScale}%`,
					height: `${100 / sheetScale}%`,
					backgroundColor: "black",
				}}
			>
				<SheetBackground
					ref={setRef}
					bottom={bottom}
					right={right}
					gridPixelSize={gridPixelSize}
					onSelectionDragStart={onSelectionDragStart}
					onMovementDragStart={onSheetDragStart}
					onStartBlockCreation={onStartBlockCreation}
				/>
				<div
					style={{
						position: "absolute",
						background: "#00F3",
						left: `calc(50% - ${sheetPosition.x}px)`,
						top: `calc(50% - ${sheetPosition.y}px - 1px)`,
					}}
				>
					{selectionRect && (
						<div
							id="selectionRect"
							ref={selectionRef}
							style={{
								position: "fixed", // or 'absolute' relative to a positioned parent
								left: selectionRect.x,
								top: selectionRect.y,
								width: selectionRect.width,
								height: selectionRect.height,
								border: "1px dashed blue",
								backgroundColor: "rgba(0, 0, 255, 0.1)",
								pointerEvents: "none", // Allow mouse events to pass through
							}}
						/>
					)}
					{sheet.connections.map((connection, index) => {
						return (
							<ConnectionLine
								key={`connection-${index}`}
								connection={connection}
								sheetIndex={props.sheetIndex}
								connectionIndex={index}
								hasError={connection.error}
							/>
						);
					})}
					{visibleOperators.map((operator, index) => {
						return (
							<Block
								blockIndex={sheet.operators.indexOf(operator)}
								key={operator.id}
								hasError={operator.error}
								onDoubleClick={() => {
									const windowSize = {
										x: 500,
										y: 400,
									};

									const windowPosition = {
										x: (window.innerWidth - windowSize.x) / 2,
										y: (window.innerHeight - windowSize.y) / 2,
									};

									dispatch(
										createWindow({
											typeAndProps: {
												type: "blockConfig",
												props: {
													source: {
														operatorId: operator.id,
														sheetIndex: props.sheetIndex,
														type: "operator",
													},
												},
											},
											title: operator.name,
											position: windowPosition,
											size: windowSize,
										}),
									);

									sheetWindowRef.current?.blur();
								}}
								// onClick={event => {devModeLog(event)}}
								source={{
									type: "operator",
									operatorId: operator.id,
									sheetIndex: props.sheetIndex,
								}}
								sheetIndex={props.sheetIndex}
							/>
						);
					})}
					{visibleMemoryRefs.map((memoryRef) => {
						return (
							<Block
								blockIndex={sheet.memoryRefs.indexOf(memoryRef)}
								key={`${memoryRef.id}-${memoryRef.sourceMemoryId}`}
								hasError={memoryRef.error}
								onDoubleClick={() => {
									const windowSize =
										MEMORY_WINDOW_SIZES[memoryRef.name] ??
										MEMORY_WINDOW_SIZES.DEFAULT;

									const windowPosition = {
										x: (window.innerWidth - windowSize.x) / 2,
										y: (window.innerHeight - windowSize.y) / 2,
									};

									dispatch(
										createWindow({
											typeAndProps: {
												type: "blockConfig",
												props: {
													source: {
														type: "memoryRef",
														memoryRefId: memoryRef.id,
														sheetIndex: props.sheetIndex,
													},
												},
											},
											title: memoryRef.name,
											position: windowPosition,
											size: windowSize,
										}),
									);

									sheetWindowRef.current?.blur();
								}}
								// onClick={event => {devModeLog(event)}}
								source={{
									type: "memoryRef",
									memoryRefId: memoryRef.id,
									sheetIndex: props.sheetIndex,
								}}
								sheetIndex={props.sheetIndex}
							/>
						);
					})}
					{blockPreview && (
						<BlockPreview
							ref={previewRef}
							sheetIndex={props.sheetIndex}
							blockedFromPlacing={!canPlacePreview}
						/>
					)}
				</div>
			</div>
			<InfoBox sheetPosition={sheetPosition} zoom={sheetScale} />
		</div>
	);
};
